/**
 * Auth Storage Abstraction
 *
 * Uses localStorage on native (Capacitor) for persistent login across app restarts.
 * Uses sessionStorage on web for security (clears when tab closes).
 *
 * This allows users to stay logged in after force-quitting the native app,
 * while maintaining the more secure behavior on web browsers.
 */

import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

/**
 * Centralized auth-storage key names. Use these constants instead of
 * literal strings so the key name only exists in one place — renaming
 * or rotating becomes a single edit instead of a repo-wide grep.
 */
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'xavior_auth_token',
  REFRESH_TOKEN: 'xavior_refresh_token',
  SESSION_TOKEN: 'sessionToken',
  USER_ID: 'userId',
  USER_EMAIL: 'userEmail',
  SELECTED_CONTAINER: 'xavior_selected_container',
} as const;

/**
 * Global, non-user-scoped sessionStorage flag set when a paywall/checkout gate
 * is voluntarily dismissed (see App.tsx). It is NOT in AUTH_STORAGE_KEYS because
 * it lives only in sessionStorage (never localStorage on native) and must be
 * swept on every auth teardown so a stale flag from one identity can't suppress
 * a gate for the next identity in the same tab.
 */
export const PAYWALL_DISMISSED_KEY = 'xavior:paywall-dismissed';

/**
 * Get the appropriate storage for auth tokens
 * - Native app: localStorage (persists across app restarts)
 * - Web browser: sessionStorage (clears when tab closes)
 */
function getStorage(): Storage {
  if (typeof window === 'undefined') {
    // SSR fallback - return a no-op storage
    return {
      length: 0,
      clear: () => {},
      getItem: () => null,
      key: () => null,
      removeItem: () => {},
      setItem: () => {},
    };
  }

  return isNative ? localStorage : sessionStorage;
}

/**
 * Get an auth-related item from storage
 */
export function getAuthItem(key: string): string | null {
  return getStorage().getItem(key);
}

/**
 * Set an auth-related item in storage
 */
export function setAuthItem(key: string, value: string): void {
  getStorage().setItem(key, value);
}

/**
 * Remove an auth-related item from storage
 */
export function removeAuthItem(key: string): void {
  getStorage().removeItem(key);
}

/**
 * Clear all auth-related items
 * Call this on logout to ensure clean state
 */
export function clearAuthStorage(): void {
  const storage = getStorage();
  const keysToRemove = Object.values(AUTH_STORAGE_KEYS);

  keysToRemove.forEach(key => {
    storage.removeItem(key);
  });

  // Also clear from the other storage in case of migration
  // This ensures no stale tokens exist in the wrong storage
  const otherStorage = isNative ? sessionStorage : localStorage;
  keysToRemove.forEach(key => {
    otherStorage.removeItem(key);
  });

  // The voluntary-dismissal paywall flag is a global sessionStorage key that is
  // never user-scoped — sweep it on teardown so a dismissal by one identity can't
  // suppress a (team-join / checkout) gate for the next identity in the same tab.
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(PAYWALL_DISMISSED_KEY);
    } catch {
      // sessionStorage unavailable (private mode / quota) — best effort.
    }
  }

  console.log('[authStorage] Cleared auth storage');
}

/**
 * Migrate tokens from sessionStorage to localStorage on native
 * Call this once on app startup to handle existing sessions
 */
export function migrateAuthStorage(): void {
  if (!isNative) return;

  // Check if we have a token in sessionStorage but not in localStorage
  const sessionToken = sessionStorage.getItem('xavior_auth_token');
  const localToken = localStorage.getItem('xavior_auth_token');

  if (sessionToken && !localToken) {
    console.log('[authStorage] Migrating token from sessionStorage to localStorage');
    localStorage.setItem('xavior_auth_token', sessionToken);

    // Migrate other auth items too
    const items = ['sessionToken', 'userId', 'userEmail', 'xavior_selected_container', 'xavior_refresh_token'];
    items.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value) {
        localStorage.setItem(key, value);
      }
    });
  }
}

// Export storage type info for debugging
export const authStorageType = isNative ? 'localStorage' : 'sessionStorage';
